import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { debounce } from 'lodash'

export default function useList(trans = {}) {
  const toast = useToast()
  const serverId = ref(Number(router.currentRoute.params.id))
  const serverType = ref(router.currentRoute.query.server_type || null)

  const tenantFilter = ref(null)
  const cameraFilter = ref([])
  const statusActiveFilter = ref(null)
  const camerasOnlineFilter = ref(null)
  const startDateFilter = ref(null)
  const endDateFilter = ref(null)
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const cameraOptions = computed(() => store.getters['camera_proxy/getSelectedItems'])

  store.dispatch('tenant/getAll')
  store.dispatch('camera_proxy/getAll', { deviceType: 'camera' })

  const tableColumns = [
    {
      label: 'actions.name',
      key: 'actions',
      sortable: false
    },
    {
      label: 'ai_analytics_servers.id',
      key: 'id',
      sortable: true
    },
    {
      label: 'ai_analytics_servers.device_name',
      key: 'name',
      sortable: true
    },
    {
      label: 'ai_analytics_servers.online',
      key: 'online',
      sortable: true
    },
    {
      label: 'ai_analytics_servers.on_server_status',
      key: 'on_server_status',
      sortable: true
    },
    {
      label: 'ai_analytics_servers.created_at',
      key: 'created_at',
      sortable: true
    }
  ]

  // Agregar columnas adicionales si es fire_smoke
  if (serverType.value === 'fire_smoke') {
    tableColumns.splice(tableColumns.length - 1, 0, {
      label: 'ai_analytics_servers.fire_threshold',
      key: 'fire_threshold',
      sortable: true
    })
    tableColumns.splice(tableColumns.length - 1, 0, {
      label: 'ai_analytics_servers.smoke_threshold',
      key: 'smoke_threshold',
      sortable: true
    })
    tableColumns.splice(tableColumns.length - 1, 0, {
      label: 'ai_analytics_servers.use_local_confirmation',
      key: 'use_local_confirmation',
      sortable: true
    })
  }

  const refListTable = ref(null)
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(false)
  const devicesCount = ref(0)
  const serverUuid = ref(null)

  const fetchList = (ctx, callback) => {
    store.dispatch('ai_analytics_servers/devices/getAll', {
      q: searchQuery.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_server: serverId.value,
      id_camera_proxy: cameraFilter.value,
      cameras_online: camerasOnlineFilter.value,
      status_active: statusActiveFilter.value,
      from: startDateFilter.value,
      to: endDateFilter.value,
    })
      .then(response => {
        const devices = response.data && response.data.devices ? response.data.devices : []
        devicesCount.value = response.data && response.data.devices_count ? response.data.devices_count : 0
        serverUuid.value = response.data && response.data.uuid ? response.data.uuid : null
        callback(devices)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['ai_analytics_servers.fetch_error'] || 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const restartServer = async (uuid) => {
    if (!uuid) {
      toast({
        component: ToastificationContent,
        props: {
          title: trans['ai_analytics_servers.uuid_not_found'] || 'Server UUID not found',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      return
    }

    try {
      const response = await store.dispatch('ai_analytics_servers/devices/restartServer', uuid)
      if (response && response.success) {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['ai_analytics_servers.restarted_successfully'] || 'Server restarted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } else {
        toast({
          component: ToastificationContent,
          props: {
            title: trans['ai_analytics_servers.restart_error'] || 'Error restarting server',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: trans['ai_analytics_servers.restart_error'] || 'Error restarting server',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      tenants: tenantFilter.value,
      cameras: cameraFilter.value,
      statusActive: statusActiveFilter.value,
      camerasOnline: camerasOnlineFilter.value,
      startDate: startDateFilter.value,
      endDate: endDateFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    cameraFilter.value = [];
    statusActiveFilter.value = null;
    camerasOnlineFilter.value = null;
    startDateFilter.value = null;
    endDateFilter.value = null;
    filters.value = {};
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    fetchList,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    restartServer,
    serverId,
    serverType,
    serverUuid,
    devicesCount,
    tenantFilter,
    cameraFilter,
    statusActiveFilter,
    camerasOnlineFilter,
    startDateFilter,
    endDateFilter,
    tenantOptions,
    cameraOptions,
    filtersCount,
    clearSearchFilters,
    buildFilters
  }
}
